import React, { useState } from 'react';
import { isEmpty, isNil } from 'lodash';

import { useStyles } from '~/assets/styles';
import WithConfirm from '~/components/ConfirmModal';
import { SortableTable } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import EmptyState from '~/components/core/EmptyState';
import { AddIcon, PencilIcon, TrashIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import LoadingIndicator from '~/components/LoadingIndicator';
import Switch from '~/components/ui/atoms/Switch';
import { localTimeToViewTime, serverDateToLocal } from '~/DateTimeUtils';
import type {
  CreateScheduledReportExportRequest,
  UpdateScheduledReportExportRequest,
} from '~/server_shared/openapi/data-contracts';
import { DATA_EXPORT } from '~/Types';

import type { ScheduledReportExport } from './hooks/useScheduledReportExports';
import { useScheduledReportExports } from './hooks/useScheduledReportExports';
import ScheduledReportExportDialog from './ScheduledReportExportDialog';
import { SUBSCRIPTION_RULES_TEXTS } from './texts';

const SubscriptionRulesTab: React.FC = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] = useState<ScheduledReportExport | null>(null);
  const { classes } = useStyles();

  const { schedules, isLoading, isError, createSchedule, updateSchedule, deleteSchedule, setIsActive } =
    useScheduledReportExports();

  if (isLoading || isError) {
    return <LoadingIndicator isError={isError} />;
  }

  const handleOpenAddDialog = () => {
    setScheduleToEdit(null);
    setShowDialog(true);
  };

  const handleOpenEditDialog = (schedule: ScheduledReportExport) => {
    setScheduleToEdit(schedule);
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setScheduleToEdit(null);
  };

  const handleSubmit = async (values: CreateScheduledReportExportRequest | UpdateScheduledReportExportRequest) => {
    if (scheduleToEdit) {
      const id = scheduleToEdit.id;
      if (isNil(id)) {
        throw new Error('Schedule ID is required for update');
      }
      await updateSchedule(id, values as UpdateScheduledReportExportRequest);
    } else {
      await createSchedule(values as CreateScheduledReportExportRequest);
    }
  };

  const handleDelete = async (scheduleId: number) => {
    await deleteSchedule(scheduleId);
  };

  const handleToggleActive = async (scheduleId: number, isActive: boolean) => {
    await setIsActive(scheduleId, isActive);
  };

  const columnData = [
    { id: 'id', isHidden: true, numeric: true },
    { id: 'display_name', label: SUBSCRIPTION_RULES_TEXTS.COLUMN_SUBSCRIPTION_NAME },
    { id: 'report_template_name', label: SUBSCRIPTION_RULES_TEXTS.COLUMN_TEMPLATE_NAME },
    {
      id: 'target_type',
      label: SUBSCRIPTION_RULES_TEXTS.COLUMN_TARGET,
      specialCell: (row: ScheduledReportExport) =>
        row.target_type ? DATA_EXPORT.TARGET_TYPE[row.target_type as keyof typeof DATA_EXPORT.TARGET_TYPE] : '',
    },
    {
      id: 'export_days',
      label: SUBSCRIPTION_RULES_TEXTS.COLUMN_REPEATS_ON,
      specialCell: (row: ScheduledReportExport) => {
        if (row.recurrence_type === 'monthly') {
          return row.day_of_month ? `Day ${row.day_of_month}` : '';
        }
        return row.export_days
          ? row.export_days.map((day) => DATA_EXPORT.DAYS[day as keyof typeof DATA_EXPORT.DAYS]?.abbr).join(', ')
          : '';
      },
    },
    {
      id: 'export_hour',
      label: SUBSCRIPTION_RULES_TEXTS.COLUMN_REPEATS_AT,
      specialCell: (row: ScheduledReportExport) => (row.export_hour ? localTimeToViewTime(row.export_hour) : ''),
    },
    {
      id: 'end_date',
      label: SUBSCRIPTION_RULES_TEXTS.COLUMN_END_DATE,
      specialCell: (row: ScheduledReportExport) => (row.end_date ? serverDateToLocal(row.end_date) : ''),
    },
    {
      id: 'is_active',
      label: SUBSCRIPTION_RULES_TEXTS.COLUMN_ACTIVE_SUBSCRIBE,
      specialCell: (row: ScheduledReportExport) => {
        if (isNil(row.id)) throw new Error('Schedule ID is required');
        return (
          <span>
            <Switch
              checked={row.is_active}
              disabled={false}
              onChange={() => handleToggleActive(row.id as number, !row.is_active)}
            />
          </span>
        );
      },
    },
    {
      id: 'actions',
      label: SUBSCRIPTION_RULES_TEXTS.COLUMN_ACTIONS,
      disableSort: true,
      specialCell: (row: ScheduledReportExport) => {
        if (isNil(row.id)) throw new Error('Schedule ID is required');
        return (
          <>
            <InlineIconButton
              tooltipTitle={SUBSCRIPTION_RULES_TEXTS.EDIT_TOOLTIP}
              icon={PencilIcon}
              className={`${classes.textIcon} ${classes.marginedIcon} ${classes.hoverableNonFilledIcon}`}
              onClick={() => handleOpenEditDialog(row)}
            />
            <WithConfirm
              key={row.id}
              title={SUBSCRIPTION_RULES_TEXTS.DELETE_DIALOG_TITLE}
              contentText={SUBSCRIPTION_RULES_TEXTS.DELETE_DIALOG_CONTENT}
              primaryButtonName={SUBSCRIPTION_RULES_TEXTS.DELETE_DIALOG_CONFIRM}
              shouldCloseOnPrimary={true}
            >
              <InlineIconButton
                tooltipTitle={SUBSCRIPTION_RULES_TEXTS.DELETE_TOOLTIP}
                icon={TrashIcon}
                className={`${classes.textIcon} ${classes.marginedIcon} ${classes.hoverableNonFilledIcon}`}
                onClick={() => handleDelete(row.id as number)}
              />
            </WithConfirm>
          </>
        );
      },
    },
  ];

  return (
    <>
      <div className="mb-16 flex justify-end">
        <Button color="primary" onClick={handleOpenAddDialog}>
          <AddIcon iconColor="currentColor" />
          {SUBSCRIPTION_RULES_TEXTS.ADD_NEW_BUTTON}
        </Button>
      </div>
      {!isEmpty(schedules) ? (
        <SortableTable defaultOrderColumn={0} columns={columnData} rows={schedules} />
      ) : (
        <EmptyState
          title={SUBSCRIPTION_RULES_TEXTS.EMPTY_STATE_TITLE}
          subtitle={SUBSCRIPTION_RULES_TEXTS.EMPTY_STATE_DESCRIPTION}
        />
      )}
      {showDialog ? (
        <ScheduledReportExportDialog
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
          schedules={schedules}
          scheduleToEdit={scheduleToEdit}
        />
      ) : null}
    </>
  );
};

export default SubscriptionRulesTab;
